/**
 * t446u: re-order channels in the desktop sidebar: drag a channel within its section (or among channels without a
 * section), or use Move up / Move down in its right-click menu (keyboard: the context-menu key or Shift+F10).
 * Sections are workspace-wide, so the order is too, and only people who organise sections can change it.
 */
import { createContext, useCallback, useContext, useMemo, useState, type DragEvent } from 'react';
import { REORDER_FAILED, channelMoves, dropChannelId, moveChannelId, sameOrder } from '@tradeoutlet/shared';
import { useReorderChannels } from '@tradeoutlet/data';
import { useChatSections, useWorkspaceId } from '@/lib/hooks';
import { useUi } from '@/lib/store';

/** One group of channels in sidebar order: a section, or null = channels without a section. */
export interface OrderGroup { sectionId: string | null; ids: string[] }
type Place = 'before' | 'after';
interface ChannelOrderApi {
  canReorder: boolean;
  dragging: { id: string; sectionId: string | null } | null;
  over: { id: string; place: Place } | null;
  setDragging: (d: ChannelOrderApi['dragging']) => void;
  setOver: (o: ChannelOrderApi['over']) => void;
  save: (sectionId: string | null, ids: string[]) => void;
}

const Ctx = createContext<ChannelOrderApi | null>(null);
const MIME = 'application/x-hub-channel';

export function useChannelOrderState(): ChannelOrderApi {
  const ws = useWorkspaceId();
  const sections = useChatSections();
  const toast = useUi(s => s.toast);
  const onFailed = useCallback(() => toast(REORDER_FAILED, 'error'), [toast]);
  const { reorder } = useReorderChannels(ws, onFailed);
  const [dragging, setDragging] = useState<ChannelOrderApi['dragging']>(null);
  const [over, setOver] = useState<ChannelOrderApi['over']>(null);
  const canReorder = Boolean(sections.data?.can_manage);
  const save = useCallback((sectionId: string | null, ids: string[]) => { void reorder(sectionId, ids); }, [reorder]);
  return useMemo(() => ({ canReorder, dragging, over, setDragging, setOver, save }), [canReorder, dragging, over, save]);
}

export const ChannelOrderProvider = Ctx.Provider;

/** Drag-and-drop props and Move up / Move down for one channel row (nothing when it can't be re-ordered). */
export function useRowOrder(id: string, group: OrderGroup | undefined) {
  const o = useContext(Ctx);
  if (!o || !o.canReorder || !group || group.ids.length < 2) return { drag: undefined, indicator: null as Place | null, moves: undefined };
  const sameGroup = o.dragging !== null && o.dragging.sectionId === group.sectionId && o.dragging.id !== id;
  const placeOf = (e: DragEvent<HTMLElement>): Place => {
    const r = e.currentTarget.getBoundingClientRect();
    return e.clientY < r.top + r.height / 2 ? 'before' : 'after';
  };
  const commit = (next: string[]) => { if (!sameOrder(next, group.ids)) o.save(group.sectionId, next); };
  const can = channelMoves(group.ids, id);
  return {
    drag: {
      draggable: true,
      'data-reorderable': 'true',
      onDragStart: (e: DragEvent<HTMLElement>) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData(MIME, id);
        o.setDragging({ id, sectionId: group.sectionId });
      },
      onDragOver: (e: DragEvent<HTMLElement>) => {
        if (!sameGroup) return; // only within the same section
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const place = placeOf(e);
        if (o.over?.id !== id || o.over.place !== place) o.setOver({ id, place });
      },
      onDragLeave: () => { if (o.over?.id === id) o.setOver(null); },
      onDrop: (e: DragEvent<HTMLElement>) => {
        if (!sameGroup || !o.dragging) return;
        e.preventDefault();
        commit(dropChannelId(group.ids, o.dragging.id, id, placeOf(e)));
        o.setDragging(null); o.setOver(null);
      },
      onDragEnd: () => { o.setDragging(null); o.setOver(null); },
    },
    indicator: sameGroup && o.over?.id === id ? o.over.place : null,
    moves: {
      up: can.up ? () => commit(moveChannelId(group.ids, id, -1)) : undefined,
      down: can.down ? () => commit(moveChannelId(group.ids, id, 1)) : undefined,
    },
  };
}
